import { useEffect, useMemo, useState } from "react"
import services from "../../../services/services";
import _ from "underscore";
import { decryptData, encryptData } from "../../../utils/encrypt";
import { dateConvert, formatDateObject } from "../../../utils/Helper";
import { DateObject } from "react-multi-date-picker";

export const useHitsService = ({ projectChanged }) => {

    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({ service: [], server: [] });
    const [popupData, setPopupData] = useState([]);
    const [hoveredRange, setHoveredRange] = useState([]);
    const [filterCount, setFilterCount] = useState(0);
    const [showFilter, setShowFilter] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [expand, setExpand] = useState(false);
    const [allHitOptions, setAllHitOptions] = useState([]);
    const [time, setTime] = useState({ from: null, to: null });
    const [intervalFlag, setIntervalFlag] = useState(false);
    const [defaultClicked, setDefaultClicked] = useState(true);

    useEffect(() => {
        handleDateRange(null, true);
    }, [])

    useEffect(() => {
        getData();
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            if (defaultClicked) {
                handleDateRange(null, true);
            } else {
                getData();
            }
        }, 120000);

        return () => {clearInterval(interval); sessionStorage.removeItem("inspectra_info_view");}
    }, [intervalFlag]);

    const handleDateRange = (e, defaultFlag) => {
        if (e === null) {
            const now = new DateObject();
            const oneMinuteAgo = new DateObject().subtract(1, "hour");
            const range = [new DateObject(oneMinuteAgo), new DateObject(now)];
            setHoveredRange(range);
            setTime({ from: oneMinuteAgo?.toDate(), to: now?.toDate() });
            defaultFlag && handleApplyFilters(range) && setDefaultClicked(true);
        } else {
            setHoveredRange(e);
            setDefaultClicked(false);
        }
    }

    const handleApplyFilters = (dateRange) => {
        let dates;
        if (dateRange) {
            dates = {
                from_date: dateConvert(dateRange?.[0]?.toDate(), "iso-string")?.split(".")?.[0],
                to_date: dateConvert(dateRange?.[1]?.toDate(), "iso-string")?.split(".")?.[0]
            }
        } else {
            dates = {
                from_date: formatDateObject(hoveredRange?.[0], time?.from),
                to_date: formatDateObject(hoveredRange?.[1], time?.to)
            }
        }
        const hitFilters = {
            dates: dates,
            server: filterValues?.server,
            service: filterValues?.service,
        }
        sessionStorage.setItem("hitfilters", encryptData(JSON.stringify(hitFilters)));

        // set count of filters applied
        let count = 0;
        if ((hoveredRange === undefined || _.isEmpty(hoveredRange)) && (dateRange === undefined || _.isEmpty(dateRange))) {
            count = 0;
        } else {
            count++;
        }
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        setShowFilter(false);
        sessionStorage.getItem("inspectra_info_view") && getData();
        setIntervalFlag(!intervalFlag);
    }

    const handleDefaultFilters = () => {
        handleDateRange(null, false);
        setDefaultClicked(true);
        setFilterValues((prev) => ({ ...prev, service: [], server: [] }));
    }

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject)) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("hitfilters")) || "{}");
                setFilterValues((prev) => ({ ...prev, date: filters?.dates }));
                const payload = {
                    type: 'hits',
                    fromDate: filters?.dates?.from_date,
                    toDate: filters?.dates?.to_date,
                    server: _.isEmpty(filters?.server) ? undefined : filters?.server?.toString(),
                    service_name: _.isEmpty(filters?.service) ? undefined : filters?.service?.toString(),
                    project_name: selectedProject?.project_key
                }
                const response = await services.callInspectra("GET", '/elsinspectra/top', payload);
                if (response) {
                    const data = response?.data;
                    formatData(data);
                    setLoader(false);
                    sessionStorage.setItem("inspectra_info_view", true);
                }
            }
        } catch (error) {
            console.error("Error getting Server Error Data: ", error);
            setLoader(false);
        }
    }

    const formatData = (apiData) => {
        let allOptions = [];
        const serverGroups = _.uniq(_.pluck(apiData, "server_group"));
        let serverGroupedObj = serverGroups.reduce((acc, group) => {
            const filteredData = _.filter(apiData, { server_group: group });
            acc[group] = filteredData;
            return acc;
        }, {});
        serverGroupedObj = Object.fromEntries(
            Object.entries(serverGroupedObj).sort(([, a], [, b]) => b.length - a.length)
        );
        Object?.keys(serverGroupedObj)?.forEach((group) => {
            const services = _.uniq(_.pluck(serverGroupedObj[group], "service_name"));
            let xAxisCategory = [];
            services?.forEach((item) => {
                let serviceObj = {
                    name: "",
                    count: 0
                }
                const findService = _.filter(serverGroupedObj[group], { service_name: item });
                serviceObj.name = item;
                serviceObj.count = findService?.length;
                if (_.isEmpty(_.findWhere(xAxisCategory, { name: item }))) {
                    xAxisCategory.push(serviceObj);
                }
            })

            xAxisCategory = _.sortBy(xAxisCategory, "count")?.reverse();

            const servers = _.uniq(_.pluck(serverGroupedObj[group], "server"));
            let yAxis = [];
            servers?.forEach((server) => {
                let obj = {
                    name: server,
                    data: []
                }
                xAxisCategory?.forEach((service) => {
                    let sum = 0;
                    const findData = _.filter(serverGroupedObj[group], { service_name: service?.name, server: server });
                    findData?.forEach((request) => {
                        sum += parseFloat(request?.requesthits);
                    })
                    obj.data.push(sum);
                })
                yAxis.push(obj);
            })

            const combineData = _.pluck(yAxis, "data");
            const flatData = combineData?.flat();
            const xAxis = _.pluck(xAxisCategory, "name");

            const options = generateOptions(xAxis, yAxis, Math.max(...flatData), group, serverGroupedObj[group]);
            allOptions.push(options);
        })
        setAllHitOptions(allOptions);
    }

    const generateOptions = (x_axis, y_axis, max_data, group, api_data) => {
        const colorPalette = [
            '#1f77b4', '#ff7700ff', '#2cd62cff', '#d62728',
            '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
            '#bcbd22', '#17becf'
        ];

        const coloredSeries = y_axis?.map((seriesItem, idx) => ({
            ...seriesItem,
            color: colorPalette[idx % colorPalette.length]
        }));

        return {
            credits: { enabled: false },
            chart: {
                type: 'line',
                height: 250,
                events: {
                    render: function () {
                        const chart = this;
                        chart.xAxis[0].ticks &&
                            Object.values(chart.xAxis[0].ticks).forEach(tick => {
                                if (tick.label && !tick.label.clickAttached) {
                                    tick.label.element.style.cursor = 'pointer';
                                    const categoryName = tick.label.textStr;
                                    tick.label.element.onclick = () => {
                                        handleShowDetailsPopupHit(categoryName, true, api_data);
                                    };
                                    tick.label.clickAttached = true;
                                }
                            });
                    }
                }
            },
            chartSize: x_axis.length > 20 ? 'large' : x_axis.length > 10 ? 'medium' : 'small',
            title: {
                text: `<span class="p-2">${group}</span>`,
                useHTML: true,
                style: {
                    fontSize: '16px',
                    color: '#333',
                    backgroundColor: "#e5e5e5",
                },
            },
            xAxis: {
                categories: x_axis,
                labels: {
                    rotation: -45,
                    align: 'right',
                },
            },
            yAxis: {
                min: 0,
                max: max_data,
                tickInterval: max_data / 5,
                title: {
                    text: '',
                },
            },
            plotOptions: {
                line: {
                    groupPadding: 0.1,
                    point: {
                        events: {
                            click: function (e) {
                                handleShowDetailsPopupHit(e, false, api_data);
                            }
                        }
                    },
                },
            },
            tooltip: {
                style: {
                    zIndex: 9998
                },
                formatter: function () {
                    return `<div><span style="font-size:12px;">Server: <b>${this?.series?.name} </b><span style="color:${this.color};">\u25CF</span></span><br/></div><br/> Service: <b>${this?.x}</b><br/>No. of Hits: <b>${this.point.y} </b>`;
                }
            },
            legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                floating: false,
                backgroundColor: 'white',
                shadow: false,
                maxHeight: 47,
                itemMarginBottom: 4,
                navigation: {
                    activeColor: '#000',
                    animation: true,
                    arrowSize: 10,
                    inactiveColor: '#ccc',
                    style: {
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: '12px'
                    }
                },
            },
            series: coloredSeries
        };
    }

    const handleShowDetailsPopupHit = (event, isCategoryName, api_data) => {
        let serviceName = "";
        let serverName = "";
        let data = []
        if (event?.point) {
            const clickedPoint = event?.point;
            serviceName = clickedPoint?.category;
            serverName = clickedPoint?.series?.name;
            data = _.filter(api_data, { service_name: serviceName, server: serverName });
        } else if (isCategoryName) {
            serviceName = event;
            data = _.filter(api_data, { service_name: serviceName });
        }
        setPopupData(data);
        setShowPopup(true);
    }

    return useMemo(() => ({
        filterCount, loader, showFilter, setShowFilter, filterValues, setFilterValues, showPopup, setShowPopup, popupData, expand, setExpand, hoveredRange, setHoveredRange, allHitOptions, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters
    }), [filterCount, loader, showFilter, setShowFilter, filterValues, setFilterValues, showPopup, setShowPopup, popupData, expand, setExpand, hoveredRange, setHoveredRange, allHitOptions, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters])
}